// ═══════════════════════════════════════════════════════════════════════════
// mergeRecursos — fusión PURA de recursos de tarea por origen (Etapa 1).
// ───────────────────────────────────────────────────────────────────────────
// Tres fuentes (Preguntas, Opiniones, Postventa) mejoran la MISMA publicación
// y todas resuelven al tipo modificar_publicacion. En vez de que el primero
// que llega gane y los demás se salteen (dedup viejo), SUMAN sus hallazgos a
// una sola tarea por publicación, con un bloque por origen.
//
// Forma del recurso mergeado:
//   { itemId, titulo, permalink, completado,
//     hallazgosPorOrigen: { preguntas:{...}, opiniones:{...}, postventa:{...} } }
//
// Cada fuente escribe SU clave → idempotente (correr N veces converge) y no
// pisa el trabajo del operador (completado ni el bloque de otra fuente).
//
// SIN imports — función pura, testeable sin Firestore. La usa tareas.js dentro
// de upsertRecursosEnTarea (envoltorio transaccional).
// ═══════════════════════════════════════════════════════════════════════════

// Estados en los que una tarea sigue "viva" para recibir merge (una cerrada
// arranca un ciclo nuevo). Lista única — el agente y el modal la comparten.
export const ESTADOS_TAREA_ACTIVA = ['propuesto', 'pendiente', 'no_iniciada', 'en_curso', 'en_revision', 'pausada']

/**
 * Convierte un recurso entrante (con hallazgos/metricas planos) al shape con
 * el payload anidado bajo su origen.
 */
export function recursoConOrigen(recurso, origen) {
  const r = recurso || {}
  const payload = {}
  if (r.hallazgos) payload.hallazgos = r.hallazgos
  if (r.metricas) payload.metricas = r.metricas
  // Campos sueltos que algunas fuentes adjuntan (banda horaria, etc.)
  for (const k of ['tipo', 'bandaKey', 'tiempoPromMin']) {
    if (r[k] != null) payload[k] = r[k]
  }
  return {
    itemId: r.itemId != null ? String(r.itemId) : null,
    titulo: r.titulo || null,
    permalink: r.permalink || null,
    productoId: r.productoId || null,
    completado: r.completado || false,
    hallazgosPorOrigen: { [origen]: payload },
  }
}

/**
 * Fusiona recursos entrantes en los existentes, por itemId, escribiendo el
 * payload bajo la clave del origen. No pisa `completado` ni el bloque de otro
 * origen. Idempotente por origen (re-escribe su propia clave).
 *
 * @param {Array}  existentes — recursos ya en la tarea
 * @param {Array}  entrantes  — recursos nuevos (con hallazgos/metricas)
 * @param {string} origen     — 'preguntas' | 'opiniones' | 'postventa' | 'manual' | area
 * @returns {{ recursos: Array, agregados: number, actualizados: number }}
 */
export function mergearRecursosPorOrigen(existentes, entrantes, origen) {
  const out = Array.isArray(existentes) ? existentes.map(r => ({ ...r })) : []
  const idxPorItem = new Map()
  out.forEach((r, i) => { if (r?.itemId != null) idxPorItem.set(String(r.itemId), i) })
  let agregados = 0, actualizados = 0
  for (const ent of (Array.isArray(entrantes) ? entrantes : [])) {
    const nuevo = recursoConOrigen(ent, origen)
    const key = nuevo.itemId != null ? String(nuevo.itemId) : null
    if (key != null && idxPorItem.has(key)) {
      const i = idxPorItem.get(key)
      const prev = out[i]
      out[i] = {
        ...prev,
        // completar metadata si falta, pero NO pisar completado ni otros orígenes
        titulo: prev.titulo || nuevo.titulo,
        permalink: prev.permalink || nuevo.permalink,
        productoId: prev.productoId || nuevo.productoId,
        hallazgosPorOrigen: { ...(prev.hallazgosPorOrigen || {}), [origen]: nuevo.hallazgosPorOrigen[origen] },
      }
      actualizados++
    } else {
      out.push(nuevo)
      if (key != null) idxPorItem.set(key, out.length - 1)
      agregados++
    }
  }
  return { recursos: out, agregados, actualizados }
}
